import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { DashboardRange } from "./types";

export type DashboardRangeChoice = "24h" | "7d" | "30d" | "90d" | "custom";

interface DashboardContextValue {
  range: DashboardRange;
  rangeChoice: DashboardRangeChoice;
  setRangeChoice: (choice: DashboardRangeChoice) => void;
  setCustomRange: (from: string, to: string) => boolean;
  rangeError: string | null;
  refreshVersion: number;
  refresh: () => void;
  lastUpdatedAt: string | null;
  recordPanelUpdate: (updatedAt?: string | null) => void;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

const intervalForRange = (milliseconds: number): DashboardRange["interval"] => {
  if (milliseconds <= 24 * 60 * 60 * 1000) return "5m";
  if (milliseconds <= 7 * 24 * 60 * 60 * 1000) return "1h";
  return "1d";
};

const makeRange = (
  choice: DashboardRangeChoice,
  custom: { from: string; to: string } | null,
  now: Date,
): DashboardRange => {
  const to = now;
  let from: Date;
  if (choice === "custom" && custom) {
    from = new Date(custom.from);
    const customTo = new Date(custom.to);
    return {
      from: from.toISOString(),
      to: customTo.toISOString(),
      interval: intervalForRange(customTo.getTime() - from.getTime()),
    };
  }
  const hours = choice === "24h" ? 24 : null;
  const days = choice === "7d" ? 7 : choice === "30d" ? 30 : 90;
  from = new Date(
    to.getTime() -
      (hours ? hours * 60 * 60 * 1000 : days * 24 * 60 * 60 * 1000),
  );
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    interval: intervalForRange(to.getTime() - from.getTime()),
  };
};

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [rangeChoice, setRangeChoiceState] =
    useState<DashboardRangeChoice>("30d");
  const [customRange, setCustomRangeState] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [rangeAnchorMs, setRangeAnchorMs] = useState(() => Date.now());
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const range = useMemo(
    () => makeRange(rangeChoice, customRange, new Date(rangeAnchorMs)),
    [rangeChoice, customRange, rangeAnchorMs],
  );

  const setRangeChoice = useCallback((choice: DashboardRangeChoice) => {
    setRangeChoiceState(choice);
    setRangeError(null);
  }, []);

  const setCustomRange = useCallback((fromValue: string, toValue: string) => {
    const parseUtc = (value: string) =>
      new Date(value.endsWith("Z") ? value : `${value}Z`);
    const from = parseUtc(fromValue);
    const to = parseUtc(toValue);
    const duration = to.getTime() - from.getTime();
    if (
      Number.isNaN(from.getTime()) ||
      Number.isNaN(to.getTime()) ||
      duration <= 0
    ) {
      setRangeError("Choose a valid UTC start and end time.");
      return false;
    }
    if (duration > 90 * 24 * 60 * 60 * 1000) {
      setRangeError("Custom ranges can be no longer than 90 days.");
      return false;
    }
    setCustomRangeState({ from: from.toISOString(), to: to.toISOString() });
    setRangeChoiceState("custom");
    setRangeError(null);
    return true;
  }, []);

  const refresh = useCallback(() => {
    setRefreshVersion((value) => value + 1);
    setRangeAnchorMs(Date.now());
  }, []);

  const recordPanelUpdate = useCallback((updatedAt?: string | null) => {
    if (!updatedAt) return;
    setLastUpdatedAt((current) =>
      !current || new Date(updatedAt).getTime() > new Date(current).getTime()
        ? updatedAt
        : current,
    );
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        range,
        rangeChoice,
        setRangeChoice,
        setCustomRange,
        rangeError,
        refreshVersion,
        refresh,
        lastUpdatedAt,
        recordPanelUpdate,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error(
      "useDashboardContext must be used within DashboardProvider",
    );
  }
  return context;
};
